import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { FileModel } from '@ca/core/models';
import { WindowRef, WorkflowStateService } from '@ca/core/services';
import { ArivisService } from 'src/app/core/services/arivis.service';
import * as FileUtils from '../../../../utils/file/file-utils';
import * as UrlUtils from '../../../../utils/url/url-utils';
import { APEER_BLOB_STORAGE_URL_PART } from '../../constants/constants';

@Component({
    selector: 'ca-arivis-img-viewer',
    templateUrl: './arivis-img-viewer.component.html',
    styleUrls: ['./arivis-img-viewer.component.scss'],
})
export class ArivisImgViewerComponent implements OnChanges, OnDestroy {
    @Input() file: FileModel;
    @Input() imageUrl: string;
    @Input() fileExtension?: string;

    arivisUrl: string;
    showArivisViewer: boolean = true;
    showErrorMessage?: boolean;

    get isLocalhost(): boolean {
        return this.windowRef.isLocalhost;
    }

    constructor(
        private workflowStateService: WorkflowStateService,
        private windowRef: WindowRef,
        private arivisService: ArivisService,
    ) {}

    ngOnDestroy(): void {
        // #341058 This(_previousArivisViewerImage, _rerun) is dirty fix for arivis request being sent twice which
        // happens because components are being destroyed and recreated for every clone.
        // This will get resolved once we refactor cloning run steps when reporting status and changing parameters
        // according to #342183.
        if (!this.workflowStateService.rerun) {
            this.workflowStateService.previousArivisViewerImage = undefined;
        }
        this.workflowStateService.rerun = false;
    }

    ngOnChanges(changes: SimpleChanges): void {
        // 2 ways to use component, either using imageUrl or file
        let shouldInit: boolean = false;
        if (changes.imageUrl && changes.imageUrl.currentValue) {
            const { previousValue, currentValue } = changes.imageUrl;
            if (
                previousValue !== currentValue &&
                currentValue &&
                (!this.workflowStateService.previousArivisViewerImage ||
                    typeof this.workflowStateService.previousArivisViewerImage === 'object' ||
                    (typeof this.workflowStateService.previousArivisViewerImage === 'string' &&
                        this.workflowStateService.previousArivisViewerImage !== this.imageUrl))
            ) {
                shouldInit = true;
            }
            this.workflowStateService.previousArivisViewerImage = this.imageUrl;
        } else if (changes.file && changes.file.currentValue && this.file) {
            const { previousValue, currentValue } = changes.file;
            if (
                previousValue !== currentValue &&
                currentValue &&
                (currentValue.url || currentValue.token) &&
                (!this.workflowStateService.previousArivisViewerImage ||
                    typeof this.workflowStateService.previousArivisViewerImage === 'string' ||
                    this.isPreviousAndCurrentFileDifferent())
            ) {
                shouldInit = true;
            }
            this.workflowStateService.previousArivisViewerImage = this.file;
        }
        if (shouldInit) {
            this.initArivisViewer();
        }
    }

    private initArivisViewer(): void {
        this.showErrorMessage = false;

        // We need to disable and enable this flag (= reinitialize iFrame)
        // That's the only way to display url changes.
        this.showArivisViewer = false;

        let fileUrl: string | undefined = this.imageUrl;
        if (this.imageUrl == null && this.file != null && (this.file.url != null || this.file.token != null)) {
            fileUrl = this.file.url || this.file.token;
        }

        if (fileUrl != null) {
            // If fileExtension is not passed, try to get it out of the URL
            const fileExtension = FileUtils.getFileExtension(fileUrl);
            if (this.fileExtension == null && fileExtension != null) {
                this.fileExtension = fileExtension;
            }

            const isBlobStorageUrl = fileUrl.includes(APEER_BLOB_STORAGE_URL_PART);

            if (this.fileExtension != null && isBlobStorageUrl && this.arivisService.isSupportedByArivis(this.fileExtension)) {
                // Support both ways: If a file is provided, always initialize with file (more secure)
                // If not, fallback to solution with URL
                if (this.file != null) {
                    if (this.file.id) {
                        this.arivisUrl = this.arivisService.generateArivisMlViewerUrl(this.file, undefined, true);
                    } else {
                        // for public workflow file doesn't have an id,
                        // in that situation we need to use url for arivis
                        fileUrl = this.file.url || this.file.token;
                        this.arivisUrl = this.arivisService.generateArivisViewerUrl(fileUrl, true);
                    }
                } else if (fileUrl != null) {
                    this.arivisUrl = this.arivisService.generateArivisViewerUrl(fileUrl, true);
                }
            } else {
                this.showErrorMessage = true;
            }

            this.showArivisViewer = true;
        }
    }

    private isPreviousAndCurrentFileDifferent(): boolean {
        if (typeof this.workflowStateService.previousArivisViewerImage === 'object') {
            const sameById: boolean =
                this.workflowStateService.previousArivisViewerImage.id != null &&
                this.file.id != null &&
                this.workflowStateService.previousArivisViewerImage.id !== this.file.id;
            const sameByToken: boolean =
                this.workflowStateService.previousArivisViewerImage.token != null &&
                this.file.token != null &&
                UrlUtils.removeQueryParameter(this.workflowStateService.previousArivisViewerImage.token) !== UrlUtils.removeQueryParameter(this.file.token);
            const sameByUrl: boolean =
                this.workflowStateService.previousArivisViewerImage.url != null &&
                this.file.url != null &&
                this.workflowStateService.previousArivisViewerImage.url !== this.file.url;
            return sameById || sameByToken || sameByUrl;
        } else {
            return false;
        }
    }
}
